/* eslint-disable react/require-default-props */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../shared-functions/LocalStorage';
import charger, { AdhocSessionPayload } from '../utils/api/charger';

/**
 * Interface representing an active price component from tariffs API.
 */
export interface ActivePriceComponent {
  type: string;
  price: number | string;
  vat?: number | string;
  step_size?: number;
}

/**
 * Interface representing tariff fee details from tariffs[0].fees.
 */
export interface TariffFeeDetails {
  platformFee?: number | string | null;
  platformFeeType?: string;
  paymentSurcharge?: number | string | null;
  paymentSurchargeType?: string;
  sessionFee?: number | string | null;
  sessionFeeType?: string;
  currency?: string;
  platformFeeMissing?: boolean;
  platformFeeByCurrency?: Array<{
    currency: string;
    amount: number;
  }>;
}

/**
 * Interface representing an individual tariff item from tariffs array.
 */
export interface TariffItem {
  tariff_id?: string;
  currency?: string;
  tariff_currency?: string;
  fx_rate?: number;
  fx_rate_source?: string;
  tariff_type?: string;
  vat?: number | string;
  active_price_components?: ActivePriceComponent[];
  elements?: Array<{
    price_components?: ActivePriceComponent[];
  }>;
  fees?: TariffFeeDetails;
  platformFee?: number | string;
  platform_fee?: number | string;
  platformFeeType?: string;
  paymentSurcharge?: number | string;
  surchargeFee?: number | string;
  surcharge?: number | string;
  sessionFee?: number | string;
  sessionFeeType?: string;
}

/**
 * Normalized tariff fee structure exposed to consuming components.
 */
export interface TariffFees {
  platformFee: number;
  platformFeeType: string;
  sessionFee: number;
  sessionFeeType: string;
  currencyType: string;
  vat?: number | string;
}

/**
 * Type contract for the Tariff Context.
 */
export interface TariffContextType {
  rawTariffData: any | null;
  tariffItem: TariffItem | null;
  energyPrice: string;
  vat: number | string;
  fees: TariffFees | null;
  isFreeCharging: boolean;
  isZeroTariff: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  refreshTariff: () => Promise<void>;
}

const TariffContext = createContext<TariffContextType | undefined>(undefined);

/**
 * Helper to generate and store a new ad-hoc session token when required.
 * @param payload - Optional session parameters.
 * @returns Ad-hoc session token or null.
 */
const refreshAdhocToken = async (
  payload?: AdhocSessionPayload,
): Promise<string | null> => {
  try {
    const response = await charger.createAdhocSession(payload);
    const data = response?.data?.data || response?.data;
    if (data?.token) {
      setLocalStorageItem('token', data.token);
      setLocalStorageItem('adhocToken', data.token);
      if (data?.adhocUid) {
        setLocalStorageItem('adhocUid', data.adhocUid);
      }
      return data.token;
    }
  } catch (error) {
    console.error('TariffContext: Error refreshing adhoc token:', error);
  }
  return null;
};

/**
 * Properties for the TariffProvider component.
 */
export interface TariffProviderProperties {
  children: React.ReactNode;
  /** Optional explicit EVSE identifier override */
  evseId?: string;
}

/**
 * TariffProvider component managing centralized EV tariff and fee states.
 * Autonomously fetches tariffs, parses components, and handles token expiration retries.
 * @param properties - Component properties.
 * @param properties.children - Child React nodes.
 * @param properties.evseId - Optional explicit EVSE identifier override.
 * @returns JSX Element wrapping children with TariffContext.
 */
export function TariffProvider({
  children,
  evseId: explicitEvseId,
}: TariffProviderProperties): JSX.Element {
  const routeParameters = useParams<{ deviceId?: string }>();
  const location = useLocation();

  const queryParameters = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const resolvedDeviceId = useMemo(() => {
    if (explicitEvseId && explicitEvseId.trim() !== '') {
      return explicitEvseId.trim();
    }
    if (routeParameters.deviceId && routeParameters.deviceId.trim() !== '') {
      return routeParameters.deviceId.trim();
    }
    const queryDeviceId = queryParameters.get('deviceId');
    if (queryDeviceId && queryDeviceId.trim() !== '') {
      return queryDeviceId.trim();
    }
    const storedDeviceId = getLocalStorageItem('deviceId');
    if (storedDeviceId && String(storedDeviceId).trim() !== '') {
      return String(storedDeviceId).trim();
    }
    return '';
  }, [explicitEvseId, routeParameters.deviceId, queryParameters]);

  const connectorIdFromDevice = useMemo(() => {
    const connectorFromQuery = queryParameters.get('connectorId');
    if (connectorFromQuery) return connectorFromQuery;
    if (resolvedDeviceId.includes('_')) {
      const lastIndex = resolvedDeviceId.lastIndexOf('_');
      const suffix = resolvedDeviceId.substring(lastIndex + 1);
      if (suffix) return suffix;
    }
    return undefined;
  }, [queryParameters, resolvedDeviceId]);

  const [rawTariffData, setRawTariffData] = useState<any | null>(null);
  const [tariffItem, setTariffItem] = useState<TariffItem | null>(null);
  const [energyPrice, setEnergyPrice] = useState<string>('');
  const [vat, setVat] = useState<number | string>(0);
  const [fees, setFees] = useState<TariffFees | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Parses raw tariff API payload and normalizes rates and fee objects.
   * @param responseData - Raw response from tariffs endpoint.
   */
  const processTariffPayload = useCallback((responseData: any): void => {
    setRawTariffData(responseData);

    const firstTariff: TariffItem | null =
      responseData?.tariffs?.[0] ||
      (Array.isArray(responseData) ? responseData[0] : null);

    const topLevelFees = responseData?.fees;

    setTariffItem(firstTariff);

    const tariffPlatformFee = Number(
      firstTariff?.fees?.platformFee ??
        firstTariff?.platformFee ??
        firstTariff?.platform_fee ??
        0,
    );
    const tariffSurchargeFee = Number(
      firstTariff?.fees?.paymentSurcharge ??
        firstTariff?.fees?.sessionFee ??
        firstTariff?.paymentSurcharge ??
        firstTariff?.surchargeFee ??
        firstTariff?.surcharge ??
        firstTariff?.sessionFee ??
        0,
    );

    const topPlatformFee = Number(topLevelFees?.platformFee ?? 0);
    const topSessionFee = Number(
      topLevelFees?.sessionFee ??
        topLevelFees?.surchargeFee ??
        topLevelFees?.paymentSurcharge ??
        0,
    );

    const effectivePlatformFee = tariffPlatformFee || topPlatformFee || 0;
    const effectiveSessionFee = tariffSurchargeFee || topSessionFee || 0;

    const platformFeeType =
      firstTariff?.fees?.platformFeeType ||
      firstTariff?.platformFeeType ||
      topLevelFees?.platformFeeType ||
      'amount';

    const sessionFeeType =
      firstTariff?.fees?.paymentSurchargeType ||
      firstTariff?.fees?.sessionFeeType ||
      firstTariff?.sessionFeeType ||
      topLevelFees?.sessionFeeType ||
      'percentage';

    const currencyType =
      firstTariff?.currency ||
      firstTariff?.fees?.currency ||
      topLevelFees?.currency ||
      topLevelFees?.currencyType ||
      'SAR';

    const energyComponent =
      firstTariff?.active_price_components?.find(
        (comp: ActivePriceComponent) => comp.type?.toUpperCase() === 'ENERGY',
      ) ||
      firstTariff?.elements?.[0]?.price_components?.find(
        (comp: ActivePriceComponent) => comp.type?.toUpperCase() === 'ENERGY',
      );

    const tariffVat =
      energyComponent?.vat !== undefined
        ? energyComponent.vat
        : (firstTariff?.vat ??
          firstTariff?.elements?.[0]?.price_components?.[0]?.vat ??
          responseData?.vat ??
          0);

    const parsedPrice =
      energyComponent?.price !== undefined ? String(energyComponent.price) : '';

    setEnergyPrice(parsedPrice);
    setVat(tariffVat);
    setFees({
      platformFee: effectivePlatformFee,
      platformFeeType,
      sessionFee: effectiveSessionFee,
      sessionFeeType,
      currencyType,
      vat: tariffVat,
    });
  }, []);

  /**
   * Fetches tariff data by EVSE device identifier and retries if ad-hoc token expired.
   */
  const fetchTariff = useCallback(async (): Promise<void> => {
    if (!resolvedDeviceId) {
      setIsLoading(false);
      setIsLoaded(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    const sessionPayload: AdhocSessionPayload = {
      evseUid: resolvedDeviceId,
      ...(connectorIdFromDevice && { connectorId: connectorIdFromDevice }),
    };

    try {
      const response = await charger.fetchTariffByEvse(resolvedDeviceId);
      const data = response?.data?.data || response?.data;
      if (data) {
        processTariffPayload(data);
      }
    } catch (tariffError: any) {
      const isTokenError =
        tariffError?.response?.status === 401 ||
        tariffError?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        tariffError?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError) {
        await refreshAdhocToken(sessionPayload);
        try {
          const retryResponse = await charger.fetchTariffByEvse(resolvedDeviceId);
          const retryData = retryResponse?.data?.data || retryResponse?.data;
          if (retryData) {
            processTariffPayload(retryData);
            return;
          }
        } catch (retryError: any) {
          console.error('TariffContext: Retry failed:', retryError);
          setError(retryError?.message || 'Failed to fetch tariff on retry');
        }
      } else {
        console.error('TariffContext: Error fetching tariff:', tariffError);
        setError(tariffError?.message || 'Failed to fetch tariff');
      }
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  }, [resolvedDeviceId, connectorIdFromDevice, processTariffPayload]);

  useEffect(() => {
    fetchTariff();
  }, [fetchTariff]);

  const numericPrice = Number(energyPrice || 0);
  const numericVat = Number(vat || 0);

  // When tariffs price and vat is 0, tariff is zero
  const isZeroTariff = isLoaded && numericPrice === 0 && numericVat === 0;

  // True free charging requires 0 platform fee, 0 session fee, and 0 energy price
  const isFreeCharging = useMemo(() => {
    if (!isLoaded || !fees) return false;
    const platformZero = Number(fees.platformFee ?? 0) === 0;
    const sessionZero = Number(fees.sessionFee ?? 0) === 0;
    const energyZero = numericPrice === 0;
    return platformZero && sessionZero && energyZero;
  }, [isLoaded, fees, numericPrice]);

  const contextValue = useMemo<TariffContextType>(
    () => ({
      rawTariffData,
      tariffItem,
      energyPrice,
      vat,
      fees,
      isFreeCharging,
      isZeroTariff,
      isLoading,
      isLoaded,
      error,
      refreshTariff: fetchTariff,
    }),
    [
      rawTariffData,
      tariffItem,
      energyPrice,
      vat,
      fees,
      isFreeCharging,
      isZeroTariff,
      isLoading,
      isLoaded,
      error,
      fetchTariff,
    ],
  );

  return (
    <TariffContext.Provider value={contextValue}>
      {children}
    </TariffContext.Provider>
  );
}

/**
 * Fallback refresh handler when called outside TariffProvider.
 * @returns {Promise<void>} Resolves immediately.
 */
const fallbackRefreshTariff = async (): Promise<void> => {};

/**
 * Custom React hook to access centralized tariff and fee information.
 * Returns safe fallback values if mounted outside a TariffProvider (e.g., in unit tests).
 * @returns {TariffContextType} Current tariff context value.
 */
export function useTariff(): TariffContextType {
  const context = useContext(TariffContext);
  if (!context) {
    return {
      rawTariffData: null,
      tariffItem: null,
      energyPrice: '',
      vat: 0,
      fees: null,
      isFreeCharging: false,
      isZeroTariff: false,
      isLoading: false,
      isLoaded: false,
      error: null,
      refreshTariff: fallbackRefreshTariff,
    };
  }
  return context;
}

export default TariffContext;
